import { useIntl } from '@weenat/wintl';
import isNil from 'lodash-es/isNil';
import { parse } from 'query-string-for-all';
import { useMemo } from 'react';
import { AlertsNotifType, crossOrgNotifications, DevicesNotifType, NotificationType, OrgAdminNotifType, TipsNotifType, UserAccountNotifType } from '../resources/notifications';
/**
 * Gets a parameter by its name from a url string
 */
export function getParameterByNameFromLink(name, url) {
    const [, params] = url.split('?');
    const parsedParams = parse(params);
    return !isNil(parsedParams) ? parsedParams[name] : undefined;
}
/** Using Sets below to avoid duplicates */
export const TIPS_NOTIFICATIONS_SET = Object.values(TipsNotifType);
export const USER_ACCOUNT_NOTIFICATIONS_SET = Object.values(UserAccountNotifType);
export const DEVICES_NOTIFICATIONS_SET = Object.values(DevicesNotifType);
export const ALERTS_NOTIFICATIONS_SET = Object.values(AlertsNotifType);
export const ORG_ADMIN_NOTIFICATIONS_SET = Object.values(OrgAdminNotifType);
export const CLICKABLE_NOTIFICATIONS = [
    ...crossOrgNotifications,
    NotificationType.adminCallForJoiningNetwork,
    NotificationType.adminCallForJoiningNetworkAsTrial,
    NotificationType.networkAdminInviteSubscription,
    NotificationType.optionPurchaseRequest,
    ...ALERTS_NOTIFICATIONS_SET,
    ...DEVICES_NOTIFICATIONS_SET
];
export const isClickableNotificationType = (typeInt) => {
    return CLICKABLE_NOTIFICATIONS.includes(typeInt);
};
function throwUndefinedLinkForNotificationError(type) {
    throw Error(`Link is clickable but no link specified for notification type ${type}`);
}
const legacyAdminPathRegex = /\/administration\/organizations\/(\d+)\/(.+)/;
/**
 * Gets an application path based on notification type and content
 */
export const getPathFromNotification = (notification) => {
    let result = undefined;
    const { type, link, networkId } = notification;
    result = !isNil(link)
        ? link.replaceAll('https://app-preprod.weenat.com', '').replaceAll('https://app.weenat.com', '')
        : undefined;
    if (isClickableNotificationType(type)) {
        switch (type) {
            case NotificationType.lowTemperatureOnSingleMeasurement:
            case NotificationType.highTemperatureOnSingleMeasurement:
            case NotificationType.highRainfallsOnSingleMeasurement:
            case NotificationType.highHumidityOnSingleMeasurement:
            case NotificationType.lowHumidityOnSingleMeasurement:
            case NotificationType.highWindOnSingleMeasurement:
            case NotificationType.lowWindOnSingleMeasurement:
            case NotificationType.highGustWindOnSingleMeasurement:
            case NotificationType.lowGustWindOnSingleMeasurement:
            case NotificationType.highDryTemperatureOnSingleMeasurement:
            case NotificationType.lowDryTemperatureOnSingleMeasurement:
            case NotificationType.highWetTemperatureOnSingleMeasurement:
            case NotificationType.lowWetTemperatureOnSingleMeasurement:
            case NotificationType.oadRainBeginningAndEnd:
            case NotificationType.highTemperatureOnCumul:
            case NotificationType.highRainfallsOnCumul:
            case NotificationType.rainfallInFollowingPeriod:
            case NotificationType.leafWetnessDurationCumul:
            case NotificationType.observedHydricPotentialState:
            case NotificationType.observedSoilMoistureStateCapa:
            case NotificationType.highRainfallsIntensity:
            case NotificationType.leafWetnessBeginEnd:
            case NotificationType.highLeafWetnessMeasurement:
            case NotificationType.lowLeafWetnessMeasurement:
            case NotificationType.tavmillsContaminationLevelChange:
            case NotificationType.multiPredicatesMeasurement:
            case NotificationType.tavmillsContamination:
                // result is already good for those notifications
                break;
            case NotificationType.adminInviteMembership:
            case NotificationType.networkAdminInviteSubscriptionExpert:
            case NotificationType.networkAdminInviteSubscriptionExpertAsTrial:
            case NotificationType.networkAdminInviteSubscription:
            case NotificationType.networkAdminInviteSubscriptionAsTrial:
                result = '/me/invitations/';
                break;
            case NotificationType.adminCallForJoiningNetwork:
            case NotificationType.adminCallForJoiningNetworkAsTrial:
                result = `/farms/${networkId}/admin/network/membership-requests/`;
                break;
            case NotificationType.optionPurchaseRequest:
                result = `/farms/${networkId}/admin/network/option-requests/`;
                break;
            case NotificationType.deviceEventGps:
            case NotificationType.deviceEventLockedSpoon:
            case NotificationType.deviceEventLockedSpoon10Events:
            case NotificationType.deviceEventNoMessages:
            case NotificationType.deviceEventNoMessagesEnd:
            case NotificationType.deviceEventNotVertical:
                if (!isNil(link)) {
                    const redirectFromLink = getParameterByNameFromLink('redirect', link);
                    if (!isNil(redirectFromLink) && redirectFromLink.startsWith('/administration')) {
                        result = redirectFromLink.replace(legacyAdminPathRegex, `/farms/$1/admin/$2`);
                    }
                }
                break;
            default:
                // Force switch to be complete
                throwUndefinedLinkForNotificationError(type);
        }
    }
    return result;
};
export const useNotificationFilterCategories = () => {
    const { t } = useIntl();
    return useMemo(() => [
        {
            id: 'alerts',
            name: t('models.notificationPreference.categories.measure.title'),
            description: t('models.notificationPreference.categories.measure.filter_description'),
            types: Array.from(ALERTS_NOTIFICATIONS_SET)
        },
        {
            id: 'equipment-park',
            name: t('models.notificationPreference.categories.device.title'),
            description: t('models.notificationPreference.categories.device.filter_description'),
            types: Array.from(DEVICES_NOTIFICATIONS_SET)
        },
        {
            id: 'org-administration',
            name: t('models.notificationPreference.categories.org_admin.title'),
            description: t('models.notificationPreference.categories.org_admin.filter_description'),
            types: Array.from(ORG_ADMIN_NOTIFICATIONS_SET)
        }
        /**
         * Commented below categories as those notifications are only send through emails and not push/in-app notifications
         */
        /*
        {
          id: 'user-account',
          name: t('models.notificationPreference.categories.user_account.title'),
          types: Array.from(USER_ACCOUNT_NOTIFICATIONS_SET)
        },
        {
          id: 'weenat-tips',
          name: t('models.notificationPreference.categories.tips.title'),
          types: Array.from(TIPS_NOTIFICATIONS_SET)
        }
        */
    ], []);
};
